/**
 * SUNBAY · <location> — the location card on a SUNMI register's Terminal tab
 * (shown only when the model's policy enables a sunbay_tapro reader).
 * Merchant ID (M + 10 alnum) and optional Store ID (S + 10 alnum). Not secrets,
 * so the saved values are SHOWN — but read-only until "Edit" (owner 2026-10-29):
 * it is location-wide, every CPad at the location uses it, and a stray keystroke
 * on a workstation page must never change it. With none saved, the form opens
 * straight away, because there is nothing to protect and a CPad cannot pay without one.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Store } from 'lucide-react';
import { api } from '../../../services/apiService';
import { toastService } from '../../../services/toastService';
import { RubyGemMark } from '../../RubyGemMark';
import { ConfirmLight } from '../unigate/UnigateCredentialsModal';
import { merchantProblems, PAYMENT_PROCESSORS_PATH } from './sunbayCreds';

const INPUT = 'min-h-[44px] w-full rounded-lg border border-stone-300 bg-white px-3 text-sm font-mono text-stone-900 outline-none placeholder:text-stone-400 focus:border-ruby focus:ring-2 focus:ring-rose-200';

export const SunbayLocationCard: React.FC<{ tenantId: string; locationId: string; locationName: string }> = ({ tenantId, locationId, locationName }) => {
  /** undefined = loading, null = failed. */
  const [status, setStatus] = useState<any | null | undefined>(undefined);
  const [merchantId, setMerchantId] = useState('');
  const [storeId, setStoreId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const load = useCallback(() => {
    let live = true;
    setStatus(undefined);
    api.posNmi.getLocationSunbay(tenantId, locationId)
      .then((s: any) => {
        if (!live) return;
        setStatus(s || null); setMerchantId(s?.merchantId || ''); setStoreId(s?.storeId || '');
        setEditing(!s?.set); setTouched(false);
      })
      .catch(() => { if (live) setStatus(null); });
    return () => { live = false; };
  }, [tenantId, locationId]);
  useEffect(() => load(), [load]);

  const problems = merchantProblems(merchantId, storeId);
  const invalid = Object.keys(problems).length > 0;
  const dirty = merchantId.trim() !== (status?.merchantId || '') || storeId.trim() !== (status?.storeId || '');
  const btn = 'inline-flex min-h-[44px] items-center gap-2 rounded-lg px-3 text-xs font-semibold text-ruby hover:bg-[#FFF1F3] disabled:opacity-40';

  const cancel = () => {
    setMerchantId(status?.merchantId || ''); setStoreId(status?.storeId || '');
    setTouched(false); setError(null); setEditing(false);
  };
  const save = async () => {
    setBusy(true); setError(null);
    try {
      await api.posNmi.putLocationSunbay(tenantId, locationId, { merchantId: merchantId.trim(), ...(storeId.trim() ? { storeId: storeId.trim() } : {}) });
      toastService.success('SUNBAY merchant ID saved for this location.');
      load();
    } catch (e: any) { setError(e?.serverMessage || e?.message || 'Could not save the merchant ID.'); }
    finally { setBusy(false); }
  };
  const clear = async () => {
    setBusy(true); setError(null);
    try {
      await api.posNmi.deleteLocationSunbay(tenantId, locationId);
      toastService.success('SUNBAY merchant ID removed for this location.');
      setConfirming(false);
      load();
    } catch (e: any) { setError(e?.serverMessage || e?.message || 'Could not remove the merchant ID.'); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border border-stone-200 bg-white" data-testid="sb-location-card">
      <div className="flex flex-wrap items-center gap-3 px-[18px] py-3">
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-[#FFF1F3] text-ruby"><Store size={16} /></span>
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-semibold text-stone-900">SUNBAY · {locationName}</div>
          <span data-testid="sb-line" className="inline-flex flex-wrap items-center gap-2 text-[12.5px] text-stone-700">
            <span className={`h-2 w-2 shrink-0 rounded-full ${status?.set ? 'bg-emerald-500' : status === undefined || status === null ? 'bg-stone-300' : 'bg-ruby'}`} />
            {status === undefined ? 'Checking…' : status === null ? 'Could not check'
              : status.set ? (
                <span>
                  Merchant ID <span className="font-mono font-medium text-stone-900" data-testid="sb-merchant-value">{status.merchantId}</span>
                  {status.storeId && <> · Store <span className="font-mono font-medium text-stone-900" data-testid="sb-store-value">{status.storeId}</span></>}
                </span>
              ) : <>Merchant ID missing · <Link to={PAYMENT_PROCESSORS_PATH} className="font-medium text-ruby-deep underline-offset-2 hover:underline">platform credentials</Link></>}
          </span>
        </div>
        {status === undefined && <RubyGemMark size={18} drawing color="currentColor" />}
        {status?.set && !editing && (
          <button type="button" className={btn} onClick={() => setEditing(true)} data-testid="sb-edit">Edit</button>
        )}
      </div>
      {editing && status !== undefined && (
        <>
          <div className="grid grid-cols-1 gap-3 border-t border-stone-200 px-[18px] py-3 md:grid-cols-2">
            <div>
              <span className="mb-1 block text-xs font-medium text-stone-700">Merchant ID</span>
              <input className={INPUT} aria-label="Merchant ID" value={merchantId} placeholder="M0123456789" autoComplete="off" data-testid="sb-merchant"
                onChange={(e) => { setMerchantId(e.target.value); setTouched(true); }} />
              {touched && problems.merchantId && <span className="mt-0.5 block text-[11px] text-ruby-deep">{problems.merchantId}</span>}
            </div>
            <div>
              <span className="mb-1 block text-xs font-medium text-stone-700">Store ID (optional)</span>
              <input className={INPUT} aria-label="Store ID" value={storeId} placeholder="S0123456789" autoComplete="off" data-testid="sb-store"
                onChange={(e) => { setStoreId(e.target.value); setTouched(true); }} />
              {touched && problems.storeId && <span className="mt-0.5 block text-[11px] text-ruby-deep">{problems.storeId}</span>}
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-stone-200 px-[18px] py-2">
            {error && <span role="alert" className="mr-auto text-[12px] text-ruby-deep">{error}</span>}
            {status?.set && <span className="mr-auto text-[11.5px] text-stone-500">Applies to every CPad at {locationName}.</span>}
            {status?.set && <button type="button" className={btn} onClick={() => setConfirming(true)} disabled={busy} data-testid="sb-clear">Remove</button>}
            {status?.set && <button type="button" className={btn} onClick={cancel} disabled={busy} data-testid="sb-cancel">Cancel</button>}
            <button type="button" data-testid="sb-save-merchant" onClick={save} disabled={busy || invalid || !dirty}
              className="inline-flex min-h-[44px] items-center gap-2 rounded-lg bg-black px-4 text-xs font-semibold text-white hover:bg-stone-800 disabled:opacity-40">
              {busy && <RubyGemMark size={16} drawing color="currentColor" />} Save
            </button>
          </div>
        </>
      )}
      {confirming && (
        <ConfirmLight title="Remove the merchant ID?" confirmLabel="Remove" busy={busy}
          message={`Every CPad at ${locationName} stops taking card payments until a merchant ID is entered again.`}
          onConfirm={clear} onCancel={() => setConfirming(false)} />
      )}
    </div>
  );
};

export default SunbayLocationCard;
